import { faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Message } from '@/types/message';
import DualTime from '@/ui/components/DualTime';

import MessageErrorIndicator from './MessageErrorIndicator';

type Field = { label: string; value: string };

function parsePreview(preview: string | null | undefined): { title: string; fields: Field[] } {
  if (!preview) return { title: '', fields: [] };

  const lines = preview.split('\n');
  const title = lines[0] ?? '';
  const fields = lines
    .slice(1)
    .map((line) => {
      const colonIdx = line.indexOf(':');
      if (colonIdx === -1) return null;
      return {
        label: line.substring(0, colonIdx).trim(),
        value: line.substring(colonIdx + 1).trim()
      };
    })
    .filter((f): f is Field => f !== null && f.value !== '' && f.value !== 'N/A');

  return { title, fields };
}

const VoiceMessage = ({ message, timezone }: { message: Message; timezone?: string | null }) => {
  const isInbound = message.direction === 'in';
  const { title, fields } = parsePreview(message.preview);
  const callLabel = title || (isInbound ? 'Incoming call' : 'Outgoing call');

  return (
    <div className={`flex w-full mb-2 ${isInbound ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`min-w-0 flex flex-col gap-2 px-3 py-2.5 rounded-lg border text-sm max-w-sm ${
          isInbound ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
        }`}>
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <div className="h-8 w-8 rounded-full flex items-center justify-center bg-green-500">
              <FontAwesomeIcon icon={faPhone} className="text-xs text-white" />
            </div>
            {message.error_message && (
              <MessageErrorIndicator errorMessage={message.error_message} />
            )}
          </div>
          <span className="flex-1 font-medium text-gray-700">{callLabel}</span>
        </div>
        {fields.length > 0 && (
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs pl-10">
            {fields.map(({ label, value }) => (
              <>
                <span key={`l-${label}`} className="text-gray-500 whitespace-nowrap">
                  {label}
                </span>
                <span key={`v-${label}`} className="mp-sensitive text-gray-700 break-all">
                  {value}
                </span>
              </>
            ))}
          </div>
        )}
        <div className="pt-2 border-t border-gray-200 text-xs text-gray-400 truncate">
          <DualTime
            dateTime={new Date(message.sent_at)}
            memberTZ={timezone}
            className="text-xs text-gray-400"
          />
          {message.created_by?.first_name && message.created_by?.last_name && (
            <>
              <span className="mx-1">·</span>
              <span>
                {message.created_by.first_name} {message.created_by.last_name}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceMessage;
